import { create } from "zustand";
import { apiFetch } from "@/lib/api";

export interface Product {
  id: string;
  name: string;
  nameAmharic: string;
  price: number;
  currency: string;
  category: string;
  inStock: boolean;
}

export type OrderStatus =
  | "pending"
  | "confirmed"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled";

export interface Order {
  id: string;
  contactName: string;
  productName: string;
  quantity: number;
  totalPrice: number;
  status: OrderStatus;
  paymentMethod: string;
  paymentVerified: boolean;
  createdAt: string;
  confirmedAt?: string;
  processingAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  trackingNumber?: string;
}

interface BusinessStats {
  totalRevenue: number;
  pendingOrders: number;
  totalProducts: number;
  totalOrders: number;
}

interface BusinessState {
  products: Product[];
  orders: Order[];
  stats: BusinessStats;
  isLoading: boolean;
}

interface BusinessActions {
  fetchProducts: () => Promise<void>;
  fetchOrders: () => Promise<void>;
  addProduct: (product: Omit<Product, "id">) => Promise<void>;
  updateOrderStatus: (
    orderId: string,
    status: OrderStatus,
    trackingNumber?: string,
  ) => Promise<void>;
  getOrderById: (orderId: string) => Order | undefined;
}

interface ApiProduct {
  id: number;
  name: string;
  nameAmharic: string | null;
  price: number;
  currency: string;
  category: string;
  inStock: boolean;
}

interface ApiProductsResponse {
  products: ApiProduct[];
  total: number;
}

interface ApiOrder {
  id: number;
  contactId: number;
  productName: string;
  quantity: number;
  totalPrice: number;
  status: OrderStatus;
  trackingNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiOrdersResponse {
  orders: ApiOrder[];
  total: number;
}

function computeStats(products: Product[], orders: Order[]): BusinessStats {
  const delivered = orders.filter((o) => o.status === "delivered");
  const pending = orders.filter((o) => o.status === "pending");

  return {
    totalRevenue: delivered.reduce((sum, o) => sum + o.totalPrice, 0),
    pendingOrders: pending.length,
    totalProducts: products.length,
    totalOrders: orders.length,
  };
}

function mapProduct(product: ApiProduct): Product {
  return {
    id: String(product.id),
    name: product.name,
    nameAmharic: product.nameAmharic ?? "",
    price: Number(product.price),
    currency: product.currency,
    category: product.category,
    inStock: product.inStock,
  };
}

function mapOrder(order: ApiOrder): Order {
  const timestampField = getStatusTimestampField(order.status);
  const statusTimestamp = timestampField ? { [timestampField]: order.updatedAt } : {};

  return {
    id: String(order.id),
    contactName: `Contact #${order.contactId}`,
    productName: order.productName,
    quantity: Number(order.quantity),
    totalPrice: Number(order.totalPrice),
    status: order.status,
    paymentMethod: "Payment sync pending",
    paymentVerified: false,
    createdAt: order.createdAt,
    trackingNumber: order.trackingNumber ?? undefined,
    ...statusTimestamp,
  };
}

function getStatusTimestampField(status: OrderStatus): keyof Order | null {
  const fields: Partial<Record<OrderStatus, keyof Order>> = {
    confirmed: "confirmedAt",
    processing: "processingAt",
    shipped: "shippedAt",
    delivered: "deliveredAt",
  };
  return fields[status] ?? null;
}

export const useBusinessStore = create<BusinessState & BusinessActions>(
  (set, get) => ({
    products: [],
    orders: [],
    stats: { totalRevenue: 0, pendingOrders: 0, totalProducts: 0, totalOrders: 0 },
    isLoading: false,

    fetchProducts: async () => {
      set({ isLoading: true });
      try {
        const response = await apiFetch<ApiProductsResponse>("/api/products?limit=100");
        const products = response.products.map(mapProduct);
        const orders = get().orders;
        set({ products, stats: computeStats(products, orders), isLoading: false });
      } catch (error) {
        set({ isLoading: false });
        throw error;
      }
    },

    fetchOrders: async () => {
      set({ isLoading: true });
      try {
        const response = await apiFetch<ApiOrdersResponse>("/api/orders?limit=100");
        const orders = response.orders.map(mapOrder);
        const products = get().products;
        set({ orders, stats: computeStats(products, orders), isLoading: false });
      } catch (error) {
        set({ isLoading: false });
        throw error;
      }
    },

    addProduct: async (productData) => {
      const createdProduct = await apiFetch<ApiProduct>("/api/products", {
        method: "POST",
        body: JSON.stringify({
          name: productData.name,
          nameAmharic: productData.nameAmharic || undefined,
          price: productData.price,
          category: productData.category,
        }),
      });
      const products = [...get().products, mapProduct(createdProduct)];
      const orders = get().orders;
      set({ products, stats: computeStats(products, orders) });
    },

    updateOrderStatus: async (orderId, status, trackingNumber) => {
      const updatedOrder = await apiFetch<ApiOrder>(`/api/orders/${orderId}`, {
        method: "PATCH",
        body: JSON.stringify({ status, trackingNumber }),
      });
      const mappedOrder = mapOrder(updatedOrder);
      const orders = get().orders.map((order) =>
        order.id === orderId ? mappedOrder : order,
      );
      const products = get().products;
      set({ orders, stats: computeStats(products, orders) });
    },

    getOrderById: (orderId) => {
      return get().orders.find((o) => o.id === orderId);
    },
  }),
);
